/**
 * OrgStructureChart.tsx
 *
 * Interactive ownership diagram for Australian company structures.
 * Uses react-bootstrap ^1.6.1 components + custom CSS (BEM: .oc__*).
 *
 * Props
 * -----
 *   data        {Object}   Required - API response from getCompanyHierarchy
 *   subsidiaryData {Array} Optional - Array of subsidiary/related company JSONs (for future use)
 *   loading     {Boolean}  Optional - Shows spinner when true.
 *   error       {String}   Optional - Error message.
 *   onNodeClick {Function} Optional - Callback(nodeData) when a chart node is clicked.
 */

import React, { useState, useRef, useCallback, useMemo } from "react";
import {
  Container,
  Table,
  Modal,
  Spinner,
  Alert,
} from "react-bootstrap";
import "./OrgStructureChart.css";

// ─── Type Definitions ───────────────────────────────────────────────

interface Officer {
  OfficerName: string;
  PositionHeld: string;
  AppointedDate?: string;
  CeasedDate?: string | null;
  Code?: string;
}

interface ShareCapital {
  ISSUED: number;
  CLASS_CODE?: string;
  CLASS_DESC?: string;
  NON_VOTING_SHARES?: string;
}

interface Holder {
  CO_NAME?: string | null;
  Name?: string | null;
  GIVEN_NAME1?: string | null;
  GIVEN_NAME2?: string | null;
  GIVEN_NAME3?: string | null;
  SURNAME?: string | null;
  ACN?: string | null;
  Entity_RN?: number;
  HolderType?: string;
}

interface Holdings {
  CODE?: string;
  CLASS_CODE?: string;
  SUM_TOT_SHARES: number;
  BEN_OWNER?: boolean;
}

interface Shareholding {
  Holdings: Holdings;
  Holders: Holder[];
}

interface UnitHolding {
  Holdings: Holdings;
  Holders: Holder[];
}

interface UnitRegister {
  TrustCode: string;
  TrustName: string;
  Units: UnitHolding[];
}

interface CompanyData {
  Code: string;
  CompanyName: string;
  ACN: string;
  DateOfIncorporation?: string | null;
  Officers?: Officer[];
  ShareCapital?: ShareCapital[];
  shareholders?: Shareholding[];
  UnitRegister?: UnitRegister[];
}

interface ApiResponse {
  isSuccess: boolean;
  correlationId?: string;
  data: CompanyData[];
}

interface ProcessedShareholder {
  name: string;
  acn: string | null;
  type: "company" | "individual";
  holdings: { cls: string; shares: number }[];
  totalShares: number;
  pct: number;
}

interface ProcessedUnit {
  name: string;
  acn: string | null;
  type: "company" | "individual";
  trustName: string;
  cls: string;
  units: number;
  totalUnits: number;
  pct: number;
}

interface FocusCompany {
  name: string;
  acn: string;
  inc?: string;
  officers: Officer[];
  sc: ShareCapital[];
  votingSc: ShareCapital[];
  nvSet: Set<string>;
  trust: string | null;
}

interface NvHolder {
  name: string;
  cls: string;
  shares: number;
}

interface ProcessedData {
  fc: FocusCompany;
  tot: number;
  shIn: ProcessedShareholder[];
  units: ProcessedUnit[];
  nvHolders: NvHolder[];
}

interface LayoutNode {
  id: string;
  tp: "focus" | "co" | "ind" | "unit";
  d: FocusCompany | ProcessedShareholder | ProcessedUnit;
  x: number;
  y: number;
}

interface LayoutEdge {
  p: string;
  lb: string;
  lx: number;
  ly: number;
  c: string;
  sw: number;
}

interface LayoutData {
  nd: LayoutNode[];
  ed: LayoutEdge[];
  W: number;
  H: number;
  cx: number;
  hiLY: number;
  hbLY: number;
}

interface OrgStructureChartProps {
  data: ApiResponse | CompanyData[] | null;
  subsidiaryData?: CompanyData[];
  loading?: boolean;
  error?: string | null;
  onNodeClick?: (nodeData: FocusCompany | ProcessedShareholder | ProcessedUnit) => void;
}

// ─── Helpers ────────────────────────────────────────────────────────

const fD = (d: string | undefined | null): string =>
  d
    ? new Date(d).toLocaleDateString("en-AU", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "—";

const fN = (n: number | undefined | null): string => n?.toLocaleString("en-AU") ?? "0";

const fP = (n: number): string => {
  if (n >= 100) return "100.00%";
  if (n > 0 && n < 0.01) return "<0.01%";
  return n.toFixed(2) + "%";
};

function wrap(text: string | undefined | null, max: number): string[] {
  if (!text) return [];
  const words = text.split(/\s+/);
  const lines: string[] = [];
  let cur = "";
  words.forEach((w) => {
    if (!cur) cur = w;
    else if ((cur + " " + w).length <= max) cur += " " + w;
    else {
      lines.push(cur);
      cur = w;
    }
  });
  if (cur) lines.push(cur);
  return lines;
}

// ─── Data processing (adapted for existing API format) ──────────────

function transformApiResponse(apiResponse: ApiResponse | CompanyData[] | null): CompanyData[] {
  if (!apiResponse) return [];

  // If it's the full API response with data property
  if ("data" in apiResponse && Array.isArray(apiResponse.data)) {
    return apiResponse.data;
  }

  // If it's already a CompanyData array
  if (Array.isArray(apiResponse)) {
    return apiResponse;
  }

  return [];
}

function processData(apiData: ApiResponse | CompanyData[] | null, _subsidiaryData?: CompanyData[]): ProcessedData | null {
  const records = transformApiResponse(apiData);
  if (records.length === 0) return null;

  // Use the first company as the focus company
  const d = records[0];
  if (!d) return null;

  const allSc = d.ShareCapital || [];

  // Non-voting class codes
  const nvSet = new Set<string>(
    allSc
      .filter((c) => String(c.NON_VOTING_SHARES || "NO").toUpperCase() === "YES")
      .map((c) => c.CLASS_CODE || "")
      .filter(Boolean)
  );

  const votingSc = allSc.filter((c) => !nvSet.has(c.CLASS_CODE || ""));

  const fc: FocusCompany = {
    name: d.CompanyName,
    acn: d.ACN,
    inc: d.DateOfIncorporation || undefined,
    officers: (d.Officers || []).filter((o) => !o.CeasedDate),
    sc: allSc,
    votingSc,
    nvSet,
    trust: null, // Will be set from UnitRegister if available
  };

  // Set trust name if available
  if (d.UnitRegister && d.UnitRegister.length > 0) {
    fc.trust = d.UnitRegister[0].TrustName || null;
  }

  // Voting total
  const tot = votingSc.reduce((s, c) => s + (c.ISSUED || 0), 0);

  // Shareholders – skip non-voting
  const shMap = new Map<string, ProcessedShareholder>();
  (d.shareholders || []).forEach((sh) => {
    const h = sh.Holdings;
    const hl = sh.Holders || [];
    const classCode = h.CLASS_CODE || h.CODE || "";
    if (nvSet.has(classCode)) return;

    const isCo = hl.some((x) => x.HolderType === "C" || x.CO_NAME);
    let nm = "";
    if (hl.length === 1) {
      nm =
        hl[0].CO_NAME ||
        hl[0].Name ||
        [hl[0].GIVEN_NAME1, hl[0].GIVEN_NAME2, hl[0].SURNAME]
          .filter(Boolean)
          .join(" ");
    } else {
      nm = hl
        .map((x) =>
          x.CO_NAME || x.Name ||
          [x.GIVEN_NAME1, x.GIVEN_NAME2, x.SURNAME].filter(Boolean).join(" ")
        )
        .join(" & ");
    }
    nm = (nm || "").replace(/\s+/g, " ").trim();
    const key = hl
      .map((x) => x.Entity_RN?.toString() || x.CO_NAME || x.SURNAME || "")
      .sort()
      .join("-");

    if (!shMap.has(key)) {
      shMap.set(key, {
        name: nm,
        acn: isCo ? hl[0]?.ACN || null : null,
        type: isCo ? "company" : "individual",
        holdings: [],
        totalShares: 0,
        pct: 0,
      });
    }

    const e = shMap.get(key)!;
    e.holdings.push({ cls: classCode, shares: h.SUM_TOT_SHARES });
    e.totalShares += h.SUM_TOT_SHARES;
  });

  // Non-voting holders (for footer reference)
  const nvHolders: NvHolder[] = [];
  (d.shareholders || []).forEach((sh) => {
    const h = sh.Holdings;
    const hl = sh.Holders || [];
    const classCode = h.CLASS_CODE || h.CODE || "";
    if (!nvSet.has(classCode)) return;
    const nm =
      hl[0]?.CO_NAME ||
      hl[0]?.Name ||
      [hl[0]?.GIVEN_NAME1, hl[0]?.GIVEN_NAME2, hl[0]?.SURNAME]
        .filter(Boolean)
        .join(" ");
    nvHolders.push({ name: (nm || "").trim(), cls: classCode, shares: h.SUM_TOT_SHARES });
  });

  const shIn = Array.from(shMap.values())
    .map((s) => ({ ...s, pct: tot > 0 ? (s.totalShares / tot) * 100 : 0 }))
    .sort((a, b) => b.totalShares - a.totalShares);

  // Unit register
  const units: ProcessedUnit[] = [];
  (d.UnitRegister || []).forEach((tr) => {
    const tu = (tr.Units || []).reduce(
      (s, u) => s + (u.Holdings?.SUM_TOT_SHARES || 0),
      0
    );
    (tr.Units || []).forEach((u) => {
      const h = u.Holdings;
      const hl = u.Holders?.[0];
      if (!hl) return;

      const name = (hl.CO_NAME || hl.Name || "").trim();
      const classCode = h.CLASS_CODE || h.CODE || "";

      units.push({
        name,
        acn: hl.ACN || null,
        type: "company",
        trustName: tr.TrustName,
        cls: classCode,
        units: h.SUM_TOT_SHARES,
        totalUnits: tu,
        pct: tu > 0 ? (h.SUM_TOT_SHARES / tu) * 100 : 0,
      });
    });
  });

  return { fc, tot, shIn, units, nvHolders };
}

// ─── Layout constants ───────────────────────────────────────────────
const NW = 210;
const NHC = 110;
const NHI = 80;
const GAP = 32;

// ─── Toggle sub-component ───────────────────────────────────────────
interface TogProps {
  v: boolean;
  onChange: (val: boolean) => void;
  label: string;
}

function Tog({ v, onChange, label }: TogProps) {
  return (
    <div className="oc__t">
      <div
        className={`oc__tt ${v ? "oc__tt--on" : "oc__tt--off"}`}
        onClick={() => onChange(!v)}
      >
        <div
          className={`oc__th ${v ? "oc__th--on" : "oc__th--off"}`}
        />
      </div>
      <span className="oc__tl">{label}</span>
    </div>
  );
}

// ─── Main Component ─────────────────────────────────────────────────
export default function OrgStructureChart({
  data,
  subsidiaryData = [],
  loading = false,
  error = null,
  onNodeClick,
}: OrgStructureChartProps) {
  const [showAcn, setAcn] = useState(true);
  const [showEdge, setEdge] = useState(true);
  const [showUnits, setUnits] = useState(true);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [drag, setDrag] = useState(false);
  const [ds, setDs] = useState({ x: 0, y: 0 });
  const [sel, setSel] = useState<FocusCompany | ProcessedShareholder | ProcessedUnit | null>(null);
  const [dirShow, setDirShow] = useState(false);
  const [dirData, setDirData] = useState<{ name: string; dirs: Officer[] } | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  const p = useMemo(() => (data ? processData(data, subsidiaryData) : null), [data, subsidiaryData]);

  const hasU = p && p.units.length > 0;
  const showBot = showUnits && hasU;
  const hasNV = p ? p.fc.nvSet.size > 0 : false;
  const nvClasses = p ? p.fc.sc.filter((sc) => p.fc.nvSet.has(sc.CLASS_CODE || "")) : [];

  // ── Layout calculation ──
  const lay = useMemo((): LayoutData | null => {
    if (!p) return null;
    const sc = p.shIn.length;
    const bc = showBot ? p.units.length : 0;
    const mc = Math.max(sc, bc, 1);
    const W = Math.max(mc * (NW + GAP) - GAP + 160, 960);
    const cx = W / 2;
    const topY = 80;
    const focY = 340;
    const botY = 600;
    const nd: LayoutNode[] = [];
    const ed: LayoutEdge[] = [];

    nd.push({ id: "f", tp: "focus", d: p.fc, x: cx, y: focY });

    const busA = focY - NHC / 2 - 60;
    const focT = focY - NHC / 2;
    const hiLY = (busA + focT) / 2;
    const tsx = cx - ((sc - 1) * (NW + GAP)) / 2;
    const labA = topY + NHI / 2 + 10 + (busA - topY - NHI / 2 - 10) / 2;

    p.shIn.forEach((sh, i) => {
      const x = tsx + i * (NW + GAP);
      const nh = sh.type === "individual" ? NHI : NHC;
      nd.push({ id: "s" + i, tp: sh.type === "individual" ? "ind" : "co", d: sh, x, y: topY });
      const isMaj = sh.pct > 50;
      ed.push({
        p: `M ${x} ${topY + nh / 2} L ${x} ${busA} L ${cx} ${busA} L ${cx} ${focT}`,
        lb: fP(sh.pct),
        lx: x,
        ly: labA,
        c: isMaj ? "#0f766e" : sh.type === "company" ? "#0284c7" : "#94a3b8",
        sw: isMaj ? 2.5 : 1.5,
      });
    });

    let hbLY = 0;
    if (showBot) {
      const bsx = cx - ((bc - 1) * (NW + GAP)) / 2;
      const focB = focY + NHC / 2;
      const busB = focB + 60;
      const subT = botY - NHC / 2;
      const labB = busB + (subT - busB) / 2;
      hbLY = (focB + busB) / 2;
      p.units.forEach((u, i) => {
        const x = bsx + i * (NW + GAP);
        nd.push({ id: "u" + i, tp: "unit", d: u, x, y: botY });
        ed.push({
          p: `M ${cx} ${focB} L ${cx} ${busB} L ${x} ${busB} L ${x} ${subT}`,
          lb: fP(u.pct),
          lx: x,
          ly: labB,
          c: "#b45309",
          sw: 1.5,
        });
      });
    }

    const mY = Math.max(...nd.map((n) => n.y + NHC / 2)) + 60;
    return { nd, ed, W, H: Math.max(mY, showBot ? 690 : 440), cx, hiLY, hbLY };
  }, [p, showBot]);

  // ── Drag handlers ──
  const mD = useCallback(
    (e: React.MouseEvent) => {
      if (e.button === 0) {
        setDrag(true);
        setDs({ x: e.clientX - pan.x, y: e.clientY - pan.y });
      }
    },
    [pan]
  );
  const mM = useCallback(
    (e: React.MouseEvent) => {
      if (drag) setPan({ x: e.clientX - ds.x, y: e.clientY - ds.y });
    },
    [drag, ds]
  );
  const mU = useCallback(() => setDrag(false), []);

  const handleNodeClick = (d: FocusCompany | ProcessedShareholder | ProcessedUnit) => {
    setSel(d);
    if (onNodeClick) onNodeClick(d);
  };

  // ── Loading / Error ──
  if (loading) {
    return (
      <div className="oc d-flex justify-content-center align-items-center" style={{ minHeight: 300 }}>
        <Spinner animation="border" variant="success" />
      </div>
    );
  }
  if (error) {
    return (
      <Container className="oc py-4">
        <Alert variant="danger">{error}</Alert>
      </Container>
    );
  }
  if (!p || !lay) {
    return (
      <Container className="oc py-4">
        <Alert variant="warning">No company data available.</Alert>
      </Container>
    );
  }

  // ── SVG node renderers ──
  function coBox(
    d: FocusCompany | ProcessedShareholder | ProcessedUnit,
    x: number,
    y: number,
    focus: boolean,
    isUnit: boolean
  ) {
    const w = NW;
    const nl = wrap(d.name || "", 26);
    const nH = nl.length * 14;
    const cY = 8 + nH + 2;
    const aH = showAcn && d.acn ? 13 : 0;
    const dirs = "officers" in d ? (d.officers || []).filter((o: Officer) => o.PositionHeld === "DIRECTOR") : [];
    const dcH = focus && dirs.length > 0 ? 14 : 0;
    const h = Math.max(NHC, cY + aH + dcH + 10);
    const bdr = focus ? "#0f766e" : isUnit ? "#b45309" : "#0284c7";
    const bg = focus ? "#f0fdfa" : isUnit ? "#fffbeb" : "#f0f9ff";
    const ci = (focus ? "f" : isUnit ? "u" : "c") + Math.round(x) + "_" + Math.round(y);

    return (
      <g
        key={ci}
        transform={`translate(${x - w / 2},${y - h / 2})`}
        style={{ cursor: "pointer" }}
        onClick={() => handleNodeClick(d)}
      >
        <defs>
          <clipPath id={ci}>
            <rect width={w} height={h} rx={6} />
          </clipPath>
        </defs>
        <rect width={w} height={h} rx={6} fill={bg} stroke={bdr} strokeWidth={focus ? 2.5 : 1.5} />
        <rect width={w} height={4} rx={2} fill={bdr} />
        <g clipPath={`url(#${ci})`}>
          {nl.map((l, i) => (
            <text key={i} x={w / 2} y={16 + i * 14} textAnchor="middle" fontSize={11} fontWeight={700} fill="#0f172a">
              {l}
            </text>
          ))}
          {showAcn && d.acn && (
            <text x={w / 2} y={cY + 10} textAnchor="middle" fontSize={9} fill="#94a3b8" fontFamily="monospace">
              A.C.N {d.acn}
            </text>
          )}
          {focus && dirs.length > 0 && (
            <text
              x={w / 2}
              y={cY + aH + 12}
              textAnchor="middle"
              fontSize={9}
              fill={bdr}
              fontWeight={600}
              style={{ cursor: "pointer", textDecoration: "underline" }}
              onClick={(e) => {
                e.stopPropagation();
                setDirData({ name: d.name, dirs });
                setDirShow(true);
              }}
            >
              {dirs.length} Director{dirs.length !== 1 ? "s" : ""} ›
            </text>
          )}
        </g>
      </g>
    );
  }

  function indBox(d: ProcessedShareholder, x: number, y: number) {
    const w = NW;
    const nl = wrap(d.name || "", 26);
    const nH = nl.length * 13;
    const h = Math.max(NHI, 26 + nH + 20);
    const ci = "i" + Math.round(x) + "_" + Math.round(y);

    return (
      <g
        key={ci}
        transform={`translate(${x - w / 2},${y - h / 2})`}
        style={{ cursor: "pointer" }}
        onClick={() => handleNodeClick(d)}
      >
        <defs>
          <clipPath id={ci}>
            <rect width={w} height={h} rx={6} />
          </clipPath>
        </defs>
        <rect width={w} height={h} rx={6} fill="#fff" stroke="#cbd5e1" strokeWidth={1.2} />
        <g clipPath={`url(#${ci})`}>
          <circle cx={w / 2} cy={13} r={7} fill="#e2e8f0" />
          <circle cx={w / 2} cy={10.5} r={3} fill="#94a3b8" />
          <path d={`M ${w / 2 - 5} 19 Q ${w / 2} 15.5 ${w / 2 + 5} 19`} fill="#94a3b8" />
          {nl.map((l, i) => (
            <text key={i} x={w / 2} y={30 + i * 13} textAnchor="middle" fontSize={10} fontWeight={600} fill="#1e293b">
              {l}
            </text>
          ))}
          <text x={w / 2} y={30 + nH + 4} textAnchor="middle" fontSize={8} fill="#94a3b8">
            Individual Shareholder
          </text>
        </g>
      </g>
    );
  }

  const trustNm = p.units.length > 0 ? p.units[0].trustName : null;

  // ── Helper to check if selected item has holdings ──
  const selHasHoldings = sel && "holdings" in sel && sel.holdings;
  const selHasUnits = sel && "units" in sel && typeof sel.units === "number";

  // ── Render ──
  return (
    <div className="oc">
      {/* ── Header ── */}
      <div className="oc__hd">
        <div className="oc__hd-l">
          <div className="oc__hd-b">ORG CHART</div>
          <div className="oc__hd-s">Organisation Structure</div>
          <div className="oc__hd-t">{p.fc.name}</div>
          <div className="oc__hd-a">A.C.N {p.fc.acn}</div>
        </div>
      </div>

      {/* ── Toggles ── */}
      <div className="oc__tg">
        <Tog v={showAcn} onChange={setAcn} label="A.C.N" />
        <Tog v={showEdge} onChange={setEdge} label="Edge Labels" />
        {hasU && <Tog v={showUnits} onChange={setUnits} label="Unit Register" />}
      </div>

      {/* ── SVG Canvas ── */}
      <div className="oc__cv">
        <div
          className={`oc__ci ${drag ? "oc__cd" : "oc__cg"}`}
          onMouseDown={mD}
          onMouseMove={mM}
          onMouseUp={mU}
          onMouseLeave={mU}
        >
          <svg
            ref={svgRef}
            width={lay.W}
            height={lay.H}
            viewBox={`0 0 ${lay.W} ${lay.H}`}
            className={drag ? "" : "oc__sa"}
            style={{
              transform: `translate(${pan.x}px,${pan.y}px) scale(${zoom})`,
              transformOrigin: "center center",
            }}
          >
            <defs>
              <pattern id="oc-grid" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#f1f5f9" strokeWidth="0.5" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#oc-grid)" />

            {/* Section labels */}
            <line x1={40} y1={lay.hiLY} x2={lay.W - 40} y2={lay.hiLY} stroke="#e2e8f0" strokeWidth={1} strokeDasharray="4 3" />
            <rect x={lay.cx - 70} y={lay.hiLY - 10} width={140} height={20} rx={4} fill="#f0f9ff" stroke="#bfdbfe" strokeWidth={0.8} />
            <text x={lay.cx} y={lay.hiLY + 4} textAnchor="middle" fontSize={10} fill="#0284c7" fontWeight={600} fontFamily="monospace">
              ▲ SHARES HELD IN
            </text>

            {showBot && lay.hbLY > 0 && (
              <>
                <line x1={40} y1={lay.hbLY} x2={lay.W - 40} y2={lay.hbLY} stroke="#e2e8f0" strokeWidth={1} strokeDasharray="4 3" />
                <rect x={lay.cx - 80} y={lay.hbLY - 10} width={160} height={20} rx={4} fill="#fffbeb" stroke="#fbbf24" strokeWidth={0.8} />
                <text x={lay.cx} y={lay.hbLY + 4} textAnchor="middle" fontSize={10} fill="#b45309" fontWeight={600} fontFamily="monospace">
                  ▼ UNIT REGISTER
                </text>
              </>
            )}

            {/* Edges */}
            {lay.ed.map((e, i) => (
              <g key={i}>
                <path d={e.p} fill="none" stroke={e.c} strokeWidth={e.sw} />
                {showEdge && e.lb && (
                  <g>
                    <rect x={e.lx - 30} y={e.ly - 9} width={60} height={16} rx={3} fill="#fff" stroke={e.c} strokeWidth={0.8} />
                    <text x={e.lx} y={e.ly + 3} textAnchor="middle" fontSize={10} fontWeight={600} fill={e.c} fontFamily="monospace">
                      {e.lb}
                    </text>
                  </g>
                )}
              </g>
            ))}

            {/* Nodes */}
            {lay.nd.map((n) => {
              if (n.tp === "focus") return coBox(n.d as FocusCompany, n.x, n.y, true, false);
              if (n.tp === "co") return coBox(n.d as ProcessedShareholder, n.x, n.y, false, false);
              if (n.tp === "ind") return indBox(n.d as ProcessedShareholder, n.x, n.y);
              if (n.tp === "unit") return coBox(n.d as ProcessedUnit, n.x, n.y, false, true);
              return null;
            })}
          </svg>
        </div>

        {/* Zoom */}
        <div className="oc__zm">
          <span className="oc__zp">{Math.round(zoom * 100)}%</span>
          <input
            type="range"
            className="oc__zr"
            min="30"
            max="200"
            value={Math.round(zoom * 100)}
            onChange={(e) => setZoom(+e.target.value / 100)}
          />
          <button className="oc__zb" onClick={() => { setZoom(1); setPan({ x: 0, y: 0 }); }}>
            Reset
          </button>
        </div>
      </div>

      {/* Legend */}
      <div className="oc__lg">
        <div className="oc__li"><div className="oc__ll oc__ll--s" /><span className="oc__lt">Shareholder</span></div>
        <div className="oc__li"><div className="oc__ll oc__ll--m" /><span className="oc__lt">Majority</span></div>
        <div className="oc__li"><div className="oc__ll oc__ll--c" /><span className="oc__lt">Company</span></div>
      </div>

      {/* ── Stats ── */}
      <div className="oc__st">
        {[
          [p.shIn.length, "Voting Shareholders", "#0284c7"],
          [fN(p.tot), "Voting Shares", "#0f766e"],
          [p.fc.sc.length, "Share Classes", "#334155"],
          [p.fc.officers.length, "Officers", "#64748b"],
        ].map(([v, l, c], i) => (
          <div key={i} className="oc__sc" style={{ borderTopColor: c as string }}>
            <div className="oc__sv" style={{ color: c as string }}>{v}</div>
            <div className="oc__sl">{l}</div>
          </div>
        ))}
      </div>

      {/* ── Share Capital ── */}
      <div className="oc__sp">
        <div className="oc__sph">
          <span>Share Capital — {p.fc.name}</span>
          <span className="oc__spt">
            Voting total: <span className="oc__spv">{fN(p.tot)}</span>
          </span>
        </div>
        <div className="oc__spr">
          {p.fc.sc.map((sc, i) => {
            const isNV = p.fc.nvSet.has(sc.CLASS_CODE || "");
            return (
              <div key={i} className="oc__spc">
                <div className="oc__scc">
                  Class {sc.CLASS_CODE}
                  {isNV && <span style={{ marginLeft: 6, color: "#94a3b8", fontSize: 9 }}>NON-VOTING</span>}
                </div>
                <div className={`oc__sci ${isNV ? "" : ""}`} style={isNV ? { color: "#94a3b8" } : {}}>{fN(sc.ISSUED)}</div>
                {sc.CLASS_DESC && <div className="oc__scd">{sc.CLASS_DESC.trim()}</div>}
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Shares Held In Table ── */}
      <div className="oc__tc">
        <div className="oc__th2">▲ Shares Held In — {p.fc.name}</div>
        <Table hover>
          <thead>
            <tr>
              <th className="text-left">Shareholder</th>
              <th className="text-left">Type</th>
              <th className="text-left">Classes Held</th>
              <th className="tr">Total Shares</th>
              <th className="tr">% Ownership</th>
            </tr>
          </thead>
          <tbody>
            {p.shIn.map((sh, i) => (
              <tr key={i}>
                <td>
                  <div className="cn">{sh.name}</div>
                  {sh.acn && <div className="ca">{sh.acn}</div>}
                </td>
                <td className="ct">{sh.type === "company" ? "Company" : "Individual"}</td>
                <td>
                  {sh.holdings.map((h, j) => (
                    <div key={j} className="hl">
                      <span className="cb">{h.cls}</span>
                      <span className="cs">{fN(h.shares)}</span>
                    </div>
                  ))}
                </td>
                <td className="tr cf">{fN(sh.totalShares)}</td>
                <td className="tr">
                  <span className={`cp ${sh.pct > 50 ? "cp--m" : "cp--n"}`}>
                    {fP(sh.pct)}
                  </span>
                  <div className="pb">
                    <div
                      className={`pf ${sh.pct > 50 ? "pf--g" : "pf--y"}`}
                      style={{ width: `${Math.min(sh.pct, 100)}%` }}
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>

        {/* Non-voting footer */}
        {hasNV && (
          <div style={{ padding: "12px 16px", background: "#f8fafc", borderTop: "1px solid #e2e8f0" }}>
            <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
              <span style={{ color: "#94a3b8", fontWeight: 700 }}>*</span>
              <div>
                <span style={{ fontSize: 11, color: "#64748b" }}>Non-voting share classes excluded: </span>
                {nvClasses.map((sc, i) => (
                  <span key={i} style={{ fontSize: 11, color: "#94a3b8" }}>
                    Class {sc.CLASS_CODE} ({fN(sc.ISSUED)} shares)
                    {i < nvClasses.length - 1 ? ", " : ""}
                  </span>
                ))}
              </div>
            </div>
            {p.nvHolders.length > 0 && (
              <div style={{ marginTop: 8 }}>
                <div style={{ fontSize: 10, color: "#64748b", marginBottom: 4 }}>Non-voting holdings not shown above:</div>
                {p.nvHolders.map((h, i) => (
                  <div key={i} style={{ display: "flex", justifyContent: "space-between", fontSize: 11, padding: "2px 0" }}>
                    <span>{h.name}</span>
                    <span style={{ color: "#94a3b8" }}>
                      Class {h.cls}: {fN(h.shares)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Unit Register Table ── */}
      {hasU && (
        <div className="oc__tc">
          <div className="oc__th2" style={{ color: "#b45309", display: "flex", justifyContent: "space-between" }}>
            <span>▼ Unit Register — {trustNm}</span>
            <span className="oc__spt">
              Total: <span className="mn bd" style={{ color: "#b45309" }}>{fN(p.units[0]?.totalUnits)}</span> units
            </span>
          </div>
          <Table hover>
            <thead>
              <tr>
                <th className="text-left">Unit Holder</th>
                <th className="text-left">Class</th>
                <th className="tr">Units</th>
                <th className="tr">% of Trust</th>
              </tr>
            </thead>
            <tbody>
              {p.units.map((u, i) => (
                <tr key={i}>
                  <td>
                    <div className="cn">{u.name}</div>
                    {u.acn && <div className="ca">{u.acn}</div>}
                  </td>
                  <td>
                    <span className="cb" style={{ background: "#fffbeb", color: "#b45309" }}>{u.cls}</span>
                  </td>
                  <td className="tr cf">{fN(u.units)}</td>
                  <td className="tr">
                    <span className="cp" style={{ color: "#b45309" }}>{fP(u.pct)}</span>
                    <div className="pb">
                      <div className="pf" style={{ width: `${Math.min(u.pct, 100)}%`, background: "#b45309" }} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}

      {/* ── Side Panel ── */}
      {sel && (
        <div className="oc__pn">
          <div className="oc__pt">
            <span className="oc__ptl">Entity Details</span>
            <button className="oc__px" onClick={() => setSel(null)}>✕</button>
          </div>

          <div className="oc__en">
            <div className="oc__enn">{sel.name}</div>
            {sel.acn && <div className="oc__ena">A.C.N {sel.acn}</div>}
            {"inc" in sel && sel.inc && <div className="oc__eni">Est. {fD(sel.inc)}</div>}
            <span className={`oc__enb ${("type" in sel && sel.type === "individual") ? "oc__enb--i" : "oc__enb--c"}`}>
              {("type" in sel && sel.type === "individual") ? "Individual" : "Company"}
            </span>
          </div>

          {selHasHoldings && (
            <div className="oc__hc">
              <div className="oc__htl">Voting Holdings in {p.fc.name}</div>
              {(sel as ProcessedShareholder).holdings.map((h, i) => (
                <div key={i} className="oc__hr">
                  <span>Class {h.cls}</span>
                  <span className="mn bd">{fN(h.shares)}</span>
                </div>
              ))}
              <div className="oc__htt">
                <span>Total ({fP((sel as ProcessedShareholder).pct)})</span>
                <span className="mn">{fN((sel as ProcessedShareholder).totalShares)}</span>
              </div>
            </div>
          )}

          {selHasUnits && (
            <div className="oc__hc">
              <div className="oc__htl" style={{ color: "#b45309" }}>Unit Holdings</div>
              <div className="oc__hr">
                <span>{(sel as ProcessedUnit).cls}</span>
                <span className="mn bd">{fN((sel as ProcessedUnit).units)} units</span>
              </div>
              <div className="oc__htt" style={{ color: "#b45309" }}>
                <span>% of Trust</span>
                <span className="mn">{fP((sel as ProcessedUnit).pct)}</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Directors Modal (react-bootstrap) ── */}
      <Modal show={dirShow} onHide={() => setDirShow(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title as="h6" className="bd">Directors</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {dirData && (
            <>
              <div className="ct mb-3">{dirData.name}</div>
              {dirData.dirs.map((dr, i) => (
                <div key={i} className="oc__dr">
                  <div className="oc__da">{(dr.OfficerName || "").charAt(0)}</div>
                  <div>
                    <div className="oc__dn">{dr.OfficerName}</div>
                    <div className="oc__dp">{dr.PositionHeld}</div>
                    {dr.AppointedDate && <div className="oc__dd">Appointed: {fD(dr.AppointedDate)}</div>}
                  </div>
                </div>
              ))}
            </>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
}
